'use client';

import { Box, CloseButton, Dialog, Portal, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useIntegrationSetup } from './IntegrationSetupContext';

/** The catalog already owns a dialog. Resource selection stays within that dialog. */
export function IntegrationResourcePicker({
  open,
  busy = false,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  const { embedded } = useIntegrationSetup();
  if (embedded)
    return open ? (
      <Box py="3">
        <Text fontWeight="600" mb="3">
          {title}
        </Text>
        {children}
      </Box>
    ) : null;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={event => {
        if (event.open || !busy) onOpenChange(event.open);
      }}
      closeOnEscape={!busy}
      closeOnInteractOutside={!busy}
      lazyMount
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner p={{ base: '3', md: '6' }}>
          <Dialog.Content
            maxW="800px"
            maxH="88dvh"
            overflow="hidden"
            bg="ui.surface.card"
            borderRadius="8px"
          >
            <Dialog.Header pr="12">
              <Dialog.Title>{title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" disabled={busy} />
            </Dialog.CloseTrigger>
            <Dialog.Body overflowY="auto" pb="6">
              {children}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
